import { call, delay, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';
import moment from 'moment';

import { getSeconds } from '@elab/core/utils/user/sagaHelpers';
import logger from '@elab/core/utils/logger';
import { cookieNames } from '@elab/core/utils/constants';
import { actions as uiActions } from '@elab/core/store/ui';
import isEmpty from '@elab/core/utils/isEmpty';
import { selectors as verifyPhoneFormSelectors } from '@elab/components/connected/forms/PhoneVerificationForm/redux';
import {
    actions as updatePhoneFormAction,
    selectors as updatePhoneFormSelectors,
    types as updatePhoneFormTypes,
} from '@elab/components/connected/forms/UpdatePhoneNumberForm/redux';

import authTypes from '../auth/types';
import authSelectors from '../auth/selectors';
import analytics from '../../utils/analytics';
import authCookiesManager from '../../utils/authCookiesManager';
import { deleteCookie } from '../../utils/cookie';

import actions from './actions';
import constants from './constants';
import selectors from './selectors';
import types from './types';
import api from './api';

export function* doLoadProfile() {
    const loading = yield select(selectors.isLoading);

    if (loading) return;
    yield put(actions.setLoading(true));
    try {
        const isAnonymous = yield call(authCookiesManager.isAnonymous);
        if (isAnonymous) {
            yield put(actions.setProfile({ anonymous: true }));
        } else {
            const profile = yield call(api.doLoadProfile);
            const disableLoyaltyCheques = yield select(selectors.getDisableLoyaltyVouchers);

            if (profile.loyalty && profile.loyaltyInformation && !disableLoyaltyCheques) {
                yield put(actions.getLoyaltyCheques());
            }
            const popUpVisible = yield call(showPhonePopUp, profile.defaultAddress);
            let loginInterrupted = false;
            if (popUpVisible) {
                yield put(updatePhoneFormAction.guestUserAttemptedProfile(profile));
                const isUpdatePhonePopupEnabled = yield select(
                    updatePhoneFormSelectors.getUpdatePhonePopupEnabled,
                );
                if (isUpdatePhonePopupEnabled) {
                    yield put(updatePhoneFormAction.useAsPopup(true));
                }
                yield put(uiActions.openDialog({ name: 'update-phone-number-form-dialog' }));
                const ifCheckoutFlow = yield select(updatePhoneFormSelectors.isCheckoutFlow);
                if (ifCheckoutFlow) {
                    const { cancel, close } = yield race({
                        canContinue: take(updatePhoneFormTypes.PHONE_NUMBER_UPDATED),
                        cancel: take(updatePhoneFormTypes.INTERRUPT_LOGIN), // checkout flow
                        close: take(updatePhoneFormTypes.CLOSE_AND_LOGOUT),
                    });
                    if (close || cancel) {
                        yield put(actions.setLoading(false));
                        if (cancel) {
                            loginInterrupted = true;
                        }
                        return;
                    }
                }
            }
            if (!loginInterrupted) {
                yield put(actions.setProfile(profile));
                yield call(updateClientStatus);
            }
        }
    } catch (e) {
        deleteCookie(cookieNames.tokenType);
        yield put(actions.setProfile({ anonymous: true }));
    }

    yield put(actions.setLoading(false));
}

export function* showPhonePopUp(address) {
    yield delay(1000);
    const shouldVerifyPhone = !isEmpty(yield select(verifyPhoneFormSelectors.getData));
    const enableForNonVerifiedCustomer = yield select(selectors.getStatusForNonVerifiedCustomer);
    return (
        address &&
        ((!address.isPhoneValidated && enableForNonVerifiedCustomer && shouldVerifyPhone) ||
            !address.mobileNumber)
    );
}

function* watchGetLoyaltyChequest() {
    yield takeEvery(types.GET_LOYALTY_CHEQUES, loadLoyaltyCheques);
}

export function* loadLoyaltyCheques() {
    try {
        let data = yield call(api.getLoyaltyCheques);
        yield put(actions.loadLoyaltyCheques(data.loyaltyVouchers));
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* setLoyaltyChequeError({ payload }) {
    const loyaltyNumber = payload.loyaltyNumber;
    const errorMessage = payload.message;
    const loyaltyCheques = yield select(selectors.getLoyaltyCheques);
    loyaltyCheques.find(el => el.loyaltyNumber === loyaltyNumber).errorMessage = errorMessage;
    yield put(actions.loadLoyaltyCheques(loyaltyCheques));
}

export function* updateClientStatus() {
    const startTime = new Date().getTime();
    const customerStatusDelay = yield select(selectors.getCustomerStatusDelay);
    const customerStatusBeforeAttemptDelay = yield select(
        selectors.getCustomerStatusBeforeAttemptDelay,
    );
    const isDirtyCustomer = status => status === constants.dirty;
    const isUpdatedCustomer = status => status === constants.updated;
    let status;
    let runnerSeconds;

    do {
        const endTime = new Date().getTime();
        yield delay(customerStatusDelay);
        runnerSeconds = yield call(getSeconds, startTime, endTime);
        try {
            const customerStatus = yield call(api.getCustomerStatus);
            status = customerStatus.crmInformation ? customerStatus.crmInformation.status : null;

            // Since this is same api as getCustomerStatus but with minimum payload,
            // Just include below in safety net too..
            if (!isDirtyCustomer(status)) {
                const profile = yield call(api.doLoadProfile);
                yield put(actions.updateProfile(profile));
            }
        } catch (e) {
            status = null;
        }

        if (isDirtyCustomer(status) && runnerSeconds === customerStatusBeforeAttemptDelay / 1000) {
            !isUpdatedCustomer(status) ? yield put(actions.setCustomerStatusNotUpdated()) : null;
        }
    } while (!isUpdatedCustomer(status) && runnerSeconds < customerStatusBeforeAttemptDelay / 1000);
}

export function* acceptPrivacy() {
    const userId = yield select(selectors.getUserId);
    try {
        yield call(api.acceptPrivacyPolicy, userId);
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* setPopoverInfo() {
    const isLoginLoading = yield select(authSelectors.isLoading);
    if (isLoginLoading) {
        yield take(authTypes.SET_LOADING);
    }
    const { firstName } = yield select(selectors.getProfile);
    yield put(actions.setUserPopoverInfo(!!firstName));
}

export function* setProfile(action) {
    yield fork(setPopoverInfo);
    yield call(analytics.handleSetCustomerProfile, action.payload);

    const isPregnant = yield select(selectors.isPregnant);
    if (!isPregnant) {
        return yield put(actions.setBabyborn(false));
    }

    const pregnancyDueDate = yield select(selectors.getPregancyDueDate);
    // Date side effect should be moved to utils (to make it testable)
    const isBabyBorn = new Date(moment(pregnancyDueDate).format()) < Date.now();
    yield put(actions.setBabyborn(isBabyBorn));
}

export function* setAnonymousProfileAfterLogout() {
    yield put(actions.setProfile({ anonymous: true }));
}

export function* checkLoggedStatus() {
    const anonymous = yield select(selectors.isAnonymous);
    return !anonymous;
}

function* watchClearLastViewedProducts() {
    yield takeEvery(types.CLEAR_LAST_VIEWED_PRODUCTS, function*({ payload }) {
        try {
            yield call(api.clearLastViewedProducts, payload.url);
            yield put(actions.setLastViewedProductsCleared());
        } catch (e) {
            /* Nothing */
        }
    });
}

function* setCountryGermany() {
    const anonymous = yield select(selectors.isAnonymous);
    const payload = {
        country: 'DE',
        userAnonymous: anonymous,
    };
    try {
        yield call(api.setUserCountry, payload);
    } catch (error) {
        /* Nothing */
    }

    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href;
}

function* setCountryAustria() {
    const anonymous = yield select(selectors.isAnonymous);
    const payload = {
        country: 'AT',
        userAnonymous: anonymous,
    };
    try {
        yield call(api.setUserCountry, payload);
    } catch (error) {
        /* Nothing */
    }

    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href;
}

export default [
    takeEvery(authTypes.AFTER_LOGOUT, setAnonymousProfileAfterLogout),
    takeEvery(types.ACCEPT_PRIVACY_POLICY, acceptPrivacy),
    takeEvery(types.LOAD_PROFILE, doLoadProfile),
    takeEvery(types.SET_PROFILE, setProfile),
    takeEvery(types.SET_LOYALTY_CHEQUE_ERROR, setLoyaltyChequeError),
    takeEvery(types.CHECK_LOGGED_STATUS, checkLoggedStatus),
    takeEvery(types.SET_AUSTRIA, setCountryAustria),
    takeEvery(types.SET_GERMANY, setCountryGermany),
    watchGetLoyaltyChequest(),
    watchClearLastViewedProducts(),
];
